import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const field =
  "mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none transition-colors focus:border-slate-900 focus:ring-2 focus:ring-slate-200";

const Frame = ({ title, subtitle, children }) => (
  <div className="grid min-h-screen place-items-center bg-[#F8FAFC] px-6">
    <div className="w-full max-w-sm border border-slate-200 bg-white p-6">
      <div className="flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center bg-slate-900 text-[11px] font-bold text-white">S3</span>
        <span className="font-display text-base font-semibold">Stock360</span>
      </div>
      <h1 className="mt-6 font-display text-xl font-semibold">{title}</h1>
      <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
      {children}
    </div>
  </div>
);

export function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [details, setDetails] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invitations/${token}`)
      .then((r) => setDetails(r.data))
      .catch((e) => setError(apiError(e)));
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/invitations/accept", { token, password });
      await refresh();
      toast.success("Welcome to Stock360");
      navigate("/app");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (error && !details) {
    return (
      <Frame title="Invite unavailable" subtitle="Ask your admin for a fresh invite.">
        <p className="mt-4 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="invite-error">{error}</p>
      </Frame>
    );
  }

  return (
    <Frame
      title={details ? `Join ${details.org_name}` : "Checking your invite…"}
      subtitle={details ? `${details.email} · ${details.role.replace("_", " ")} — set a password to finish.` : ""}
    >
      {details && (
        <form onSubmit={accept} className="mt-5 space-y-4" data-testid="accept-invite-form">
          <label className="block text-sm">
            Choose a password
            <input data-testid="accept-password-input" type="password" minLength={8} required
              className={field} value={password} onChange={(e) => setPassword(e.target.value)} />
            <span className="mt-1 block text-xs text-slate-500">At least 8 characters.</span>
          </label>
          {error && <p className="bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="invite-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="accept-invite-button"
            className="w-full bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate-700 disabled:opacity-50">
            {busy ? "Joining…" : "Join and sign in"}
          </button>
        </form>
      )}
    </Frame>
  );
}

export function ChangePassword() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/invitations/change-password", form);
      await refresh();
      toast.success("Password updated");
      navigate("/app");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Frame title="Set your own password" subtitle="You signed in with a temporary password — choose a new one to continue.">
      <form onSubmit={submit} className="mt-5 space-y-4" data-testid="change-password-form">
        <label className="block text-sm">
          Temporary password
          <input data-testid="current-password-input" type="password" required className={field}
            value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} />
        </label>
        <label className="block text-sm">
          New password
          <input data-testid="new-password-input" type="password" minLength={8} required className={field}
            value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} />
        </label>
        {error && <p className="bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="change-password-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="change-password-button"
          className="w-full bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate-700 disabled:opacity-50">
          {busy ? "Saving…" : "Save password"}
        </button>
      </form>
    </Frame>
  );
}
